import { MapAdapter } from './map-basemap';
import { LABEL_ZOOM, PLACE_LABEL_ZOOM } from './map-style';
import { Bounds, ScreenPoint } from './map-types';

/**
 * Where the suburb map is looking (#256): fitting areas into the part of the map the side panel leaves clear, the
 * zoom (only crossing a label threshold needs new layers), and telling the workspace when the view moved, at most once
 * a frame. Wraps the basemap's adapter once it is open; before that every call is a no-op.
 */
export class MapCamera {
  map: MapAdapter | null = null;
  /** Pixels on the right covered by the workspace's side panel. */
  rightInset = 0;
  zoom = 10;
  /** Called (once a frame at most) as the map pans or zooms. */
  viewListener: (() => void) | null = null;

  private zoomRaf = 0;
  private viewRaf = 0;

  /** @param relayer redraws the layers when the zoom crosses a label threshold (suburb names, place names). */
  constructor(private relayer: () => void) {}

  fit(b: Bounds | null): void {
    if (b) this.map?.fitBounds(b, this.rightInset);
  }

  setView(lng: number, lat: number, zoom: number): void {
    this.map?.setView(lng, lat, zoom);
  }

  centre(): [number, number] | null {
    return this.map?.center() ?? null;
  }

  /** Lat/lng to pixels in the map element. */
  project(lng: number, lat: number): ScreenPoint | null {
    return this.map?.project(lng, lat) ?? null;
  }

  setDraggable(on: boolean): void {
    this.map?.setDraggable(on);
  }

  resize(): void {
    this.map?.resize();
  }

  onZoom(z: number): void {
    const crossed = (threshold: number) => this.zoom >= threshold !== z >= threshold;
    // Labels are the only zoom-dependent layers: relayer only when a threshold is crossed, once a frame.
    const relayer = crossed(LABEL_ZOOM) || crossed(PLACE_LABEL_ZOOM);
    this.zoom = z;
    if (!relayer || this.zoomRaf) return;
    this.zoomRaf = requestAnimationFrame(() => {
      this.zoomRaf = 0;
      this.relayer();
    });
  }

  onViewChanged(): void {
    if (!this.viewListener || this.viewRaf) return;
    this.viewRaf = requestAnimationFrame(() => {
      this.viewRaf = 0;
      this.viewListener?.();
    });
  }
}
